import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useHistory } from "react-router-dom";
import PageLayout from "../../components/PageLayout";
import Button from "../../components/Button";
import UploadModal from "../../components/VirtualGrading/UploadModal";
import { getLoginDetails } from "../../utils/storage";
import { getData, GET_USER_VIRTUAL_GRADING_ITEMS_URL, GET_VIRTUAL_GRADING_INSIGHTS_URL } from "../../utils/api";
import UploadIcon from "../../icons/upload.svg";
import CaretIcon from "../../icons/rev_carat.png";
import RefreshIcon from "../../icons/refresh.svg";
import useIsMobile from "../../hooks/useIsMobile";
import "./VirtualGrading.css";

const VirtualGrading = () => {
  const history = useHistory();
  const isMobile = useIsMobile();
  const { hierarchy } = getLoginDetails();
  const [activeTab, setActiveTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [params, setParams] = useState({
    limit: 10,
    offset: 0,
    areMoreItemsAvailable: false,
  });
  const scrollContainerRef = useRef(null);
  const scrollTimerRef = useRef(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isLoadingPrevious, setIsLoadingPrevious] = useState(false);
  const fetchingMoreRef = useRef(false);
  const fetchingPreviousRef = useRef(false);

  // Map status to API status codes: pending=1, graded=2, rejected=3, All=0
  const getStatusCode = (tab) => {
    const statusMap = {
      pending: 1,
      graded: 2,
      rejected: 3,
      all: 0,
    };
    return statusMap[tab] || 0;
  };

  // Calculate time elapsed from createdDate
  const getTimeElapsed = (createdDate) => {
    if (!createdDate) return "Unknown";
    
    const now = new Date();
    const created = new Date(createdDate);
    const diffInMs = now - created;
    const diffInSeconds = Math.floor(diffInMs / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);
    const diffInWeeks = Math.floor(diffInDays / 7);
    const diffInMonths = Math.floor(diffInDays / 30);
    const diffInYears = Math.floor(diffInDays / 365);

    if (diffInSeconds < 60) {
      return "Just now";
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes !== 1 ? "s" : ""} ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours !== 1 ? "s" : ""} ago`;
    } else if (diffInDays < 7) {
      return `${diffInDays} day${diffInDays !== 1 ? "s" : ""} ago`;
    } else if (diffInWeeks < 4) {
      return `${diffInWeeks} week${diffInWeeks !== 1 ? "s" : ""} ago`;
    } else if (diffInMonths < 12) {
      return `${diffInMonths} month${diffInMonths !== 1 ? "s" : ""} ago`;
    } else {
      return `${diffInYears} year${diffInYears !== 1 ? "s" : ""} ago`;
    }
  };

  // Convert date to epoch milliseconds - following pattern from CostSheetReport
  // For "from" date: start of day (00:00:00.000 AM)
  // For "to" date: end of day (23:59:59.999 PM)
  const getDateEpoch = (dateValue, isEndOfDay = false) => {
    if (!dateValue) return null;
    
    if (isEndOfDay) {
      // Set to end of day: 23:59:59.999 PM
      const endDt = new Date(dateValue).setHours(23, 59, 59, 999);
      return new Date(endDt).getTime();
    } else {
      // Set to start of day: 00:00:00.000 AM
      const startDt = new Date(dateValue).setHours(0, 0, 0, 0);
      return new Date(startDt).getTime();
    }
  };

  // Fetch items from API with pagination
  const fetchItems = async (status = null, resetOffset = false, append = false, prepend = false) => {
    if (!append && !prepend) {
      setIsLoading(true);
    }
    
    try {
      const statusCode = status !== null ? getStatusCode(status) : getStatusCode(activeTab);
      
      const apiParams = {
        count: params.limit,
        offset: resetOffset ? 0 : params.offset,
        status: statusCode,
      };

      // Add date filters if provided - convert to epoch milliseconds (following CostSheetReport pattern)
      if (fromDate) {
        // Convert "from" date to start of day (00:00:00.000 AM) in epoch milliseconds
        apiParams.fromDate = getDateEpoch(fromDate, false);
      }
      if (toDate) {
        // Convert "to" date to end of day (23:59:59.999 PM) in epoch milliseconds
        apiParams.toDate = getDateEpoch(toDate, true);
      }

      const response = await getData(GET_USER_VIRTUAL_GRADING_ITEMS_URL, apiParams);
      
      // Handle API response - API returns { list: [...], areMoreItemsAvailable: boolean }
      const currentOffset = resetOffset ? 0 : params.offset;
      
      if (response && Array.isArray(response.list)) {
        if (append) {
          setItems(prev => [...prev, ...response.list]);
          setParams(prev => ({
            ...prev,
            offset: prev.offset + response.list.length,
            areMoreItemsAvailable: response.areMoreItemsAvailable || false,
          }));
        } else if (prepend) {
          setItems(prev => [...response.list, ...prev]);
          setParams(prev => ({
            ...prev,
            offset: Math.max(0, prev.offset - response.list.length),
            areMoreItemsAvailable: prev.areMoreItemsAvailable,
          }));
        } else {
          setItems(response.list);
          setParams(prev => ({
            ...prev,
            offset: currentOffset,
            areMoreItemsAvailable: response.areMoreItemsAvailable || false,
          }));
        }
      } else if (Array.isArray(response)) {
        if (append) {
          setItems(prev => [...prev, ...response]);
          setParams(prev => ({
            ...prev,
            offset: prev.offset + response.length,
            areMoreItemsAvailable: false,
          }));
        } else if (prepend) {
          setItems(prev => [...response, ...prev]);
          setParams(prev => ({
            ...prev,
            offset: Math.max(0, prev.offset - response.length),
            areMoreItemsAvailable: false,
          }));
        } else {
          setItems(response);
          setParams(prev => ({
            ...prev,
            offset: currentOffset,
            areMoreItemsAvailable: false,
          }));
        }
      } else {
        if (!append && !prepend) {
          setItems([]);
          setParams(prev => ({
            ...prev,
            offset: currentOffset,
            areMoreItemsAvailable: false,
          }));
        }
      }
    } catch (error) {
      console.error("Error fetching items:", error);
      if (!append && !prepend) {
        setItems([]);
        setParams(prev => ({
          ...prev,
          areMoreItemsAvailable: false,
        }));
      }
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
      setIsLoadingPrevious(false);
    }
  };

  // Infinite scroll functions
  const fetchMoreItems = useCallback(async () => {
    if (isLoadingMore || !params.areMoreItemsAvailable || isLoading || fetchingMoreRef.current) return;
    
    fetchingMoreRef.current = true;
    setIsLoadingMore(true);
    const newOffset = params.offset + params.limit;
    
    try {
      const statusCode = getStatusCode(activeTab);
      const apiParams = {
        count: params.limit,
        offset: newOffset,
        status: statusCode,
      };

      if (fromDate) {
        apiParams.fromDate = getDateEpoch(fromDate, false);
      }
      if (toDate) {
        apiParams.toDate = getDateEpoch(toDate, true);
      }

      const response = await getData(GET_USER_VIRTUAL_GRADING_ITEMS_URL, apiParams);
      
      if (response && Array.isArray(response.list)) {
        // Deduplicate items by itemId or itemSeq
        setItems(prev => {
          const existingIds = new Set(prev.map(item => item.itemId || item.itemSeq || item.id));
          const newItems = response.list.filter(item => {
            const itemId = item.itemId || item.itemSeq || item.id;
            return !existingIds.has(itemId);
          });
          return [...prev, ...newItems];
        });
        setParams(prev => ({
          ...prev,
          offset: newOffset,
          areMoreItemsAvailable: response.areMoreItemsAvailable || false,
        }));
      }
    } catch (error) {
      console.error("Error fetching more items:", error);
    } finally {
      setIsLoadingMore(false);
      fetchingMoreRef.current = false;
    }
  }, [params.offset, params.limit, params.areMoreItemsAvailable, isLoadingMore, isLoading, activeTab, fromDate, toDate]);

  const fetchPreviousItems = useCallback(async () => {
    if (isLoadingPrevious || params.offset === 0 || isLoading || fetchingPreviousRef.current) return;
    
    fetchingPreviousRef.current = true;
    setIsLoadingPrevious(true);
    const newOffset = Math.max(0, params.offset - params.limit);
    const container = scrollContainerRef.current;
    const previousScrollHeight = container ? container.scrollHeight : 0;
    
    try {
      const statusCode = getStatusCode(activeTab);
      const apiParams = {
        count: params.limit,
        offset: newOffset,
        status: statusCode,
      };

      if (fromDate) {
        apiParams.fromDate = getDateEpoch(fromDate, false);
      }
      if (toDate) {
        apiParams.toDate = getDateEpoch(toDate, true);
      }

      const response = await getData(GET_USER_VIRTUAL_GRADING_ITEMS_URL, apiParams);
      
      if (response && Array.isArray(response.list)) {
        // Deduplicate items by itemId or itemSeq
        setItems(prev => {
          const existingIds = new Set(prev.map(item => item.itemId || item.itemSeq || item.id));
          const newItems = response.list.filter(item => {
            const itemId = item.itemId || item.itemSeq || item.id;
            return !existingIds.has(itemId);
          });
          return [...newItems, ...prev];
        });
        setParams(prev => ({
          ...prev,
          offset: newOffset,
          areMoreItemsAvailable: prev.areMoreItemsAvailable,
        }));
        
        // Maintain scroll position after prepending items
        if (container) {
          // Use requestAnimationFrame to ensure DOM has updated
          requestAnimationFrame(() => {
            const newScrollHeight = container.scrollHeight;
            const scrollDifference = newScrollHeight - previousScrollHeight;
            container.scrollTop = container.scrollTop + scrollDifference;
          });
        }
      }
    } catch (error) {
      console.error("Error fetching previous items:", error);
    } finally {
      setIsLoadingPrevious(false);
      fetchingPreviousRef.current = false;
    }
  }, [params.offset, params.limit, isLoadingPrevious, isLoading, activeTab, fromDate, toDate]);

  // Scroll handler for infinite scroll with debouncing
  const handleScroll = useCallback((e) => {
    const container = scrollContainerRef.current || e.target;
    if (!container) return;
    
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    
    // Clear previous timer
    if (scrollTimerRef.current) {
      clearTimeout(scrollTimerRef.current);
    }
    
    // Debounce scroll handler to avoid excessive API calls
    scrollTimerRef.current = setTimeout(() => {
      // Threshold for triggering load (200px from top/bottom)
      const threshold = 200;
      
      // Load more items when scrolling down (near bottom)
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      if (distanceFromBottom < threshold) {
        if (params.areMoreItemsAvailable && !isLoadingMore && !isLoading && !fetchingMoreRef.current) {
          fetchMoreItems();
        }
      }
      
      // Load previous items when scrolling up (near top)
      if (scrollTop < threshold) {
        if (params.offset > 0 && !isLoadingPrevious && !isLoading && !fetchingPreviousRef.current) {
          fetchPreviousItems();
        }
      }
    }, 100); // 100ms debounce
  }, [params.offset, params.areMoreItemsAvailable, isLoadingMore, isLoadingPrevious, isLoading, fetchMoreItems, fetchPreviousItems]);

  // State for statistics (all items count)
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    graded: 0,
    rejected: 0,
    totalValue: 0,
    avgPrice: 0,
  });

  // Fetch statistics using insights API
  const fetchStats = async () => {
    try {
      const params = {};

      // Add date filters if provided - convert to epoch milliseconds (following CostSheetReport pattern)
      if (fromDate) {
        // Convert "from" date to start of day (00:00:00.000 AM) in epoch milliseconds
        params.fromDate = getDateEpoch(fromDate, false);
      }
      if (toDate) {
        // Convert "to" date to end of day (23:59:59.999 PM) in epoch milliseconds
        params.toDate = getDateEpoch(toDate, true);
      }

      const response = await getData(GET_VIRTUAL_GRADING_INSIGHTS_URL, params);

      // API response: { pending, graded, value, avg, rejected }
      const insights = response || {};
      
      // Calculate total items from insights
      const total = (insights.pending || 0) + (insights.graded || 0) + (insights.rejected || 0);

      setStats({
        total: total,
        pending: insights.pending || 0,
        graded: insights.graded || 0,
        rejected: insights.rejected || 0,
        totalValue: insights.value || 0,
        avgPrice: insights.avg || 0,
      });
    } catch (error) {
      console.error("Error fetching stats:", error);
      // Set default stats on error
      setStats({
        total: 0,
        pending: 0,
        graded: 0,
        rejected: 0,
        totalValue: 0,
        avgPrice: 0,
      });
    }
  };

  // Fetch items when component mounts or active tab changes
  useEffect(() => {
    // Reset offset when tab changes and fetch items
    setParams(prev => ({
      ...prev,
      offset: 0,
    }));
    fetchItems(activeTab, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Attach scroll listener
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (scrollTimerRef.current) {
        clearTimeout(scrollTimerRef.current);
      }
    };
  }, [handleScroll]);

  // Fetch stats on initial mount
  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch items and stats when date filters change (debounced to avoid duplicate calls)
  useEffect(() => {
    // Skip if no dates are set (prevents unnecessary calls on mount)
    if (!fromDate && !toDate) {
      return;
    }

    // Debounce to batch rapid date changes (if user sets both dates quickly)
    const timeoutId = setTimeout(() => {
      // Reset offset when date filters change
      setParams(prev => ({
        ...prev,
        offset: 0,
      }));
      fetchItems(activeTab, true);
      fetchStats();
    }, 200);

    return () => clearTimeout(timeoutId);
  }, [fromDate, toDate]);

  // Calculate statistics from state
  const { total: totalItems, pending: pendingItems, graded: gradedItems, rejected: rejectedItems, totalValue, avgPrice } = stats;

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    // Don't automatically open modal when upload tab is clicked
    // Modal should only open when + button is clicked
  };

  const handleUploadSuccess = (newItem) => {
    console.log("handleUploadSuccess called", newItem);
    
    // Close modal immediately - following codebase pattern where modals close after successful API call
    console.log("Closing modal - setting isUploadModalOpen to false");
    setIsUploadModalOpen(false);
    
    // Switch to pending tab after successful upload
    setActiveTab("pending");
    
    // Refetch items and stats to get updated list from server
    // This ensures we have the latest data including the newly uploaded item
    // Reset offset when refreshing after upload
    setParams(prev => ({
      ...prev,
      offset: 0,
    }));
    fetchItems("pending", true);
    fetchStats();
  };

  // Items are already filtered by API based on activeTab
  const getFilteredItems = () => {
    return items;
  };

  return (
    <PageLayout 
      heading="Virtual Grading"
      icon={UploadIcon}
    >
      <div className="virtual-grading-container">
        {/* Statistics Cards */}
        <div className="vg-stats-grid">
          <div className="vg-stat-card">
            <div className="vg-stat-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="currentColor"
                viewBox="0 0 16 16"
              >
                <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
                <path d="M7.646 1.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8.5 2.707V11.5a.5.5 0 0 1-1 0V2.707L5.354 4.854a.5.5 0 1 1-.708-.708l3-3z" />
              </svg>
            </div>
            <div className="vg-stat-content">
              <div className="vg-stat-label">Total Items</div>
              <div className="vg-stat-value">{totalItems}</div>
            </div>
          </div>

          <div className="vg-stat-card">
            <div className="vg-stat-icon pending">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="currentColor"
                viewBox="0 0 16 16"
              >
                <path d="M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71V3.5z" />
                <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0z" />
              </svg>
            </div>
            <div className="vg-stat-content">
              <div className="vg-stat-label">Pending</div>
              <div className="vg-stat-value">{pendingItems}</div>
            </div>
          </div>

          <div className="vg-stat-card">
            <div className="vg-stat-icon success">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="currentColor"
                viewBox="0 0 16 16"
              >
                <path d="M1 3a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1H1zm7 8a2 2 0 1 0 0-4 2 2 0 0 0 0 4z" />
                <path d="M0 5a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H1a1 1 0 0 1-1-1V5zm3 0a2 2 0 0 1-2 2v4a2 2 0 0 1 2 2h10a2 2 0 0 1 2-2V7a2 2 0 0 1-2-2H3z" />
              </svg>
            </div>
            <div className="vg-stat-content">
              <div className="vg-stat-label">Total Value</div>
              <div className="vg-stat-value vg-price">${totalValue}</div>
            </div>
          </div>

          <div className="vg-stat-card">
            <div className="vg-stat-icon info">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="currentColor"
                viewBox="0 0 16 16"
              >
                <path d="M1 11a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1v-3zm5-4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V7zm5-5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1V2z" />
              </svg>
            </div>
            <div className="vg-stat-content">
              <div className="vg-stat-label">Avg. Price</div>
              <div className="vg-stat-value vg-price">${Math.round(avgPrice)}</div>
            </div>
          </div>
        </div>

        {/* Date Filter */}
        <div className="vg-filter-bar">
          <div className="vg-filter-content">
            <div className="vg-filter-left">
              {!isMobile && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                  className="mr-8"
                >
                  <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z" />
                </svg>
              )}
              {!isMobile && <span className="mr-12">Filter by Date:</span>}
              <input
                type="date"
                className="vg-date-input"
                placeholder="From Date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                style={{ fontSize: isMobile ? '14px' : '12px', padding: isMobile ? '8px' : '6px 10px' }}
              />
              <span className="mx-8" style={{ fontSize: isMobile ? '14px' : '12px' }}>to</span>
              <input
                type="date"
                className="vg-date-input"
                placeholder="To Date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                style={{ fontSize: isMobile ? '14px' : '12px', padding: isMobile ? '8px' : '6px 10px' }}
              />
              <button
                className="vg-reload-button"
                onClick={() => {
                  // Reset offset when reloading
                  setParams(prev => ({
                    ...prev,
                    offset: 0,
                  }));
                  fetchItems(activeTab, true);
                  fetchStats();
                }}
                title="Reload"
                style={{ padding: isMobile ? '8px' : '4px 8px' }}
              >
                <img src={RefreshIcon} alt="Reload" className="vg-reload-icon" />
              </button>
            </div>
            <button
              className="vg-upload-button"
              onClick={() => setIsUploadModalOpen(true)}
              style={{ 
                padding: isMobile ? '8px 12px' : '4px 8px',
                fontSize: isMobile ? '14px' : '11px'
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width={isMobile ? "14" : "10"}
                height={isMobile ? "14" : "10"}
                fill="currentColor"
                viewBox="0 0 16 16"
              >
                <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z" />
              </svg>
              Upload Item
            </button>
          </div>
        </div>

        {/* Status Tabs */}
        <div className="vg-tabs">
          <button
            className={`vg-tab ${activeTab === "pending" ? "active" : ""}`}
            onClick={() => handleTabClick("pending")}
          >
            Pending ({pendingItems})
          </button>
          <button
            className={`vg-tab ${activeTab === "graded" ? "active" : ""}`}
            onClick={() => handleTabClick("graded")}
          >
            Graded ({gradedItems})
          </button>
          <button
            className={`vg-tab ${activeTab === "rejected" ? "active" : ""}`}
            onClick={() => handleTabClick("rejected")}
          >
            Rejected ({rejectedItems})
          </button>
          <button
            className={`vg-tab ${activeTab === "all" ? "active" : ""}`}
            onClick={() => handleTabClick("all")}
          >
            All ({totalItems})
          </button>
        </div>

        {/* Content Area */}
        <div 
          className="vg-content" 
          ref={scrollContainerRef}
          style={{ 
            overflowY: 'auto', 
            height: isMobile ? 'calc(100vh - 400px)' : 'calc(100vh - 280px)',
            maxHeight: isMobile ? 'none' : 'calc(100vh - 280px)'
          }}
        >
          <div className="vg-items-list">
            {isLoading ? (
              <div className="vg-empty-state">
                <p>Loading...</p>
              </div>
            ) : getFilteredItems().length === 0 ? (
              <div className="vg-empty-state">
                <p>No items found</p>
              </div>
            ) : (
              <div className="vg-items-grid">
                {getFilteredItems().map((item) => {
                  // API uses itemId, not id
                  const itemId = item.itemId || item.id;
                  // Status is a number: 1=pending, 2=graded, 3=rejected
                  const status = item.status;
                  const statusString = status === 1 ? "pending" : status === 2 ? "graded" : status === 3 ? "rejected" : "pending";
                  
                  const getStatusColor = (status) => {
                    if (status === 1) return "#ffc107";
                    if (status === 2) return "#28a745";
                    if (status === 3) return "#dc3545";
                    return "#6c757d";
                  };

                  return (
                    <div 
                      key={itemId} 
                      className="vg-admin-item-card"
                    >
                      <div className="vg-item-card-header">
                        <div className="vg-item-title-section">
                          <h4 className="vg-item-title">{item.title || "Untitled Item"}</h4>
                          <div className="vg-item-id">ID: {item.itemSeq || itemId}</div>
                        </div>
                        <div className="vg-item-status-section">
                          <span
                            className="vg-status-badge"
                            style={{ backgroundColor: getStatusColor(status) }}
                          >
                            {statusString.toUpperCase()}
                          </span>
                          <button 
                            className="vg-action-btn chat"
                            onClick={(e) => {
                              e.stopPropagation();
                              // Handle chat functionality
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="10"
                              height="10"
                              fill="currentColor"
                              viewBox="0 0 16 16"
                            >
                              <path d="M14 1a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H4.414A2 2 0 0 0 3 11.586l-2 2V2a1 1 0 0 1 1-1h12zM2 0a2 2 0 0 0-2 2v12.793a.5.5 0 0 0 .854.353l2.853-2.853A1 1 0 0 1 4.414 12H14a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H2z" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      
                      <Link
                        to={`/virtual-grading/item/${itemId}`}
                        className="vg-item-details vg-clickable-section"
                      >
                        <div className="vg-detail-row">
                          <span className="vg-detail-label">Make:</span> <span className="vg-detail-value">{item.make || "N/A"}</span>
                        </div>
                        <div className="vg-detail-row">
                          <span className="vg-detail-label">Model:</span> <span className="vg-detail-value">{item.model || "N/A"}</span>
                        </div>
                        <div className="vg-detail-row">
                          <span className="vg-detail-label">Year:</span> <span className="vg-detail-value">{item.year || "N/A"}</span>
                        </div>
                        <div className="vg-detail-row">
                          <span className="vg-detail-label">Part No:</span> <span className="vg-detail-value">{item.partNo || item.partNumber || "N/A"}</span>
                        </div>
                      </Link>
                      
                      <Link
                        to={`/virtual-grading/item/${itemId}`}
                        className="vg-item-image-section vg-clickable-section"
                      >
                        <img
                          src={item.itemURL || item.itemUrl || item.image || item.images?.[0] || "https://via.placeholder.com/400x300"}
                          alt={item.title || "Item"}
                          className="vg-item-main-image"
                        />
                      </Link>
                      
                      {item.createdDate && (
                        <div className="vg-item-timestamp">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            fill="currentColor"
                            viewBox="0 0 16 16"
                            className="mr-4"
                          >
                            <path d="M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71V3.5z" />
                            <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0z" />
                          </svg>
                          {getTimeElapsed(item.createdDate)}
                        </div>
                      )}
                      
                      {(status === 2) && (item.grade || item.price || item.assessedValue) && (
                        <div className="vg-item-grade-info">
                          {item.grade && <span className="vg-grade-badge">Grade: {item.grade}</span>}
                          {(item.price || item.assessedValue || item.gradePrice) && (
                            <span className="vg-price-badge">${item.price || item.assessedValue || item.gradePrice || 0}</span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            
            {/* Loading indicators for infinite scroll */}
            {isLoadingPrevious && (
              <div className="vg-loading-indicator" style={{ textAlign: 'center', padding: '10px' }}>
                Loading previous items...
              </div>
            )}
            {isLoadingMore && (
              <div className="vg-loading-indicator" style={{ textAlign: 'center', padding: '10px' }}>
                Loading more items...
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      <UploadModal
        key={`upload-modal-${isUploadModalOpen}`}
        isOpen={isUploadModalOpen}
        onClose={() => {
          console.log("Modal close requested - setting isUploadModalOpen to false");
          setIsUploadModalOpen(false);
        }}
        onSuccess={handleUploadSuccess}
      />
    </PageLayout>
  );
};

export default VirtualGrading;

